"use client";

import { CATEGORIES } from "@/lib/data/tools";

const ABOUT_WHO = [
  {
    title: "Private practitioners",
    desc: "Carry the whole arc of care on your own — referral to discharge — without an admin team behind you.",
  },
  {
    title: "Group practices & clinics",
    desc: "Keep documentation consistent across a team while every clinician stays in their own voice.",
  },
  {
    title: "Trainees & supervisees",
    desc: "Learn the shape of good notes, formulations, and plans with drafts you can study and refine.",
  },
];

const ABOUT_STEPS = [
  {
    n: "01",
    title: "Choose a tool",
    desc: "Pick from tools organised around the stages of care, from a first referral to the final follow-up.",
  },
  {
    n: "02",
    title: "Add your context",
    desc: "Paste your notes, a referral letter, or a few lines about the client. You decide what goes in.",
  },
  {
    n: "03",
    title: "Read the draft",
    desc: "Psy.day returns a clear, structured first draft in seconds — never a blank page.",
  },
  {
    n: "04",
    title: "Make it yours",
    desc: "Adjust the wording, keep what fits, discard what doesn’t. The finished work is entirely yours.",
  },
];

const ABOUT_SAFETY = [
  {
    title: "It drafts, you decide",
    desc: "Everything Psy.day produces is a starting point for you to review. Nothing is filed, sent, or acted on without you.",
  },
  {
    title: "Not a diagnosis",
    desc: "Psy.day doesn’t diagnose and isn’t a medical device. Screening and assessment outputs support your thinking — they don’t conclude it.",
  },
  {
    title: "Your voice, your words",
    desc: "Drafts are written to be edited. The final content — clinical and ethical — always rests with you.",
  },
  {
    title: "Within your scope",
    desc: "For letters and reports, tools stay inside what’s appropriate for a clinician to say, and flag where judgment is needed.",
  },
];

const H2 =
  "mb-3.5 text-[clamp(22px,3.4vw,27px)] font-medium leading-[1.12] tracking-[-0.9px] text-ink";
const BODY =
  "w-full text-[15.5px] leading-[1.7] text-ink-2 [text-wrap:pretty]";
const CARD = "rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface p-5";
const CALLOUT =
  "mt-7 w-full rounded-[18px] border border-accent/15 bg-accent-soft px-6 py-[22px] text-[clamp(17px,2.6vw,20px)] font-medium leading-[1.42] tracking-[-0.4px] text-ink";

/** /about — product marketing / explainer page. */
export function AboutScreen() {
  return (
    <section className="animate-fade-up px-5 pt-[30px]">
      <div className="mx-auto w-full">
        <img
          src="/favicon.png"
          alt="psy.day"
          width={38}
          height={38}
          className="mb-[18px] block h-[38px] w-[38px] rounded-[11px]"
        />
        <div className="mb-3.5 inline-flex items-center gap-2 text-[12.5px] font-bold uppercase tracking-[0.9px] text-accent">
          About Psy.day
        </div>
        <h1 className="mb-[18px] text-[clamp(29px,5.5vw,42px)] font-medium leading-[1.06] tracking-[-1.6px] text-ink [text-wrap:balance]">
          More time for the person in front of you.
        </h1>
        <p className="w-full text-[clamp(16px,2.4vw,19px)] leading-[1.55] text-ink-3 [text-wrap:pretty]">
          Psy.day is a set of AI tools built for the everyday work of therapy — the
          referrals, intakes, notes, plans, and letters that gather around your sessions.
          It writes the first draft. You stay in charge of every word.
        </p>

        <img
          src="/background/coastal-scene.webp"
          alt=""
          className="mt-[34px] block h-[clamp(170px,26vw,250px)] w-full rounded-[24px] border border-[rgba(26,24,22,0.05)] object-cover [object-position:center_50%]"
        />

        {/* What Psy.day is */}
        <div className="mt-[52px]">
          <h2 className={H2}>What Psy.day is</h2>
          <p className={`${BODY} mb-3.5`}>
            Most of the work in mental health care doesn’t happen in the room. It happens
            before and after — triaging a new referral, shaping a treatment plan, writing
            up a session at the end of a long day. That work matters, and it’s quietly
            exhausting.
          </p>
          <p className={BODY}>
            Psy.day takes the blank page out of it. Tell a tool what you’re working on and
            it hands back a considered draft — a formulation, a safety plan, a progress
            note, a letter — ready for you to read, adjust, and make your own. No blank
            cursor. No starting from zero at 8pm.
          </p>
          <div className="mt-7">
            <h3 className="mb-2 text-[18.5px] font-semibold tracking-[-0.3px] text-ink">
              Our mission
            </h3>
            <p className={BODY}>
              To give clinicians back the hours that paperwork steals — so more of your
              attention can go where it belongs, to the people you care for.
            </p>
          </div>
          <div className={CALLOUT}>Software should do the typing. You do the thinking.</div>
        </div>

        {/* Who it's for */}
        <div className="mt-[56px]">
          <h2 className={H2}>Who it’s for</h2>
          <p className={BODY}>
            Psy.day is made for the people doing the clinical work — therapists,
            psychologists, and counsellors, across modalities and settings. If you write
            notes, plan treatment, or correspond about care, it was built with you in mind.
          </p>
          <div className="mt-[22px] grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]">
            {ABOUT_WHO.map((w) => (
              <div key={w.title} className={CARD}>
                <h3 className="mb-[7px] text-[16.5px] font-semibold tracking-[-0.3px] text-ink">
                  {w.title}
                </h3>
                <p className="text-[14px] leading-[1.6] text-ink-3">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="mt-[56px]">
          <h2 className={H2}>How it works</h2>
          <p className={BODY}>
            Every tool follows the same simple rhythm. You’re never more than a few steps
            from a usable draft — and never handed anything you didn’t ask for.
          </p>
          <div className="mt-[26px]">
            <h3 className="mb-3.5 text-[18.5px] font-semibold tracking-[-0.3px] text-ink">
              Every tool, the same rhythm
            </h3>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(205px,1fr))]">
              {ABOUT_STEPS.map((st) => (
                <div key={st.n} className={CARD}>
                  <div className="mb-3 text-[13px] font-bold tracking-[0.5px] text-accent">
                    {st.n}
                  </div>
                  <h4 className="mb-1.5 text-[14.5px] font-semibold tracking-[-0.15px] text-ink">
                    {st.title}
                  </h4>
                  <p className="text-[13.5px] leading-[1.55] text-ink-3">{st.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8">
            <h3 className="mb-2 text-[18.5px] font-semibold tracking-[-0.3px] text-ink">
              Tools for every stage of care
            </h3>
            <p className={`${BODY} mb-4`}>
              The library spans the full journey of an episode of care. Wherever you are in
              the work, there’s a tool shaped for it.
            </p>
            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
              {CATEGORIES.map((sg) => (
                <div
                  key={sg.name}
                  className="flex items-start gap-[11px] rounded-[16px] border border-[rgba(26,24,22,0.05)] bg-surface px-4 py-[15px]"
                >
                  <div className="mt-1.5 h-[9px] w-[9px] flex-none rounded-full bg-accent" />
                  <div className="min-w-0">
                    <h4 className="mb-[3px] text-[14px] font-semibold tracking-[-0.15px] text-ink">
                      {sg.name}
                    </h4>
                    <p className="text-[12.5px] leading-[1.5] text-muted">{sg.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* You're always in charge */}
        <div className="mt-[56px]">
          <h2 className={H2}>You’re always in charge</h2>
          <p className={BODY}>
            Psy.day is an assistant, not an authority. It’s built to support your clinical
            judgment — never to replace it. Here’s what that means in practice.
          </p>
          <div className="mt-6">
            <h3 className="mb-3.5 text-[18.5px] font-semibold tracking-[-0.3px] text-ink">
              What that means in practice
            </h3>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(255px,1fr))]">
              {ABOUT_SAFETY.map((sf) => (
                <div key={sf.title} className={CARD}>
                  <h4 className="mb-[7px] text-[15px] font-semibold tracking-[-0.2px] text-ink">
                    {sf.title}
                  </h4>
                  <p className="text-[13.5px] leading-[1.6] text-ink-3">{sf.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={CALLOUT}>
            You own every draft. Read it, change it, or throw it away — the final word is
            always yours.
          </div>
        </div>

        {/* Footer */}
        <div className="mb-2 mt-[60px] flex items-center gap-3 border-t border-[rgba(26,24,22,0.08)] pt-7">
          <img
            src="/favicon.png"
            alt="psy.day"
            width={30}
            height={30}
            className="block h-[30px] w-[30px] rounded-lg"
          />
          <div>
            <div className="text-[15px] font-semibold tracking-[-0.2px] text-ink">Psy.day</div>
            <div className="text-[13px] text-muted">The quiet assistant behind good care.</div>
          </div>
        </div>
      </div>
    </section>
  );
}
